import { useCallback } from "react";
import { Pagination, PaginationItem } from "@mui/material";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";

import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardDoubleArrowLeftRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import color from "@/styles/color";

interface IPaginationCustomProps {
  page: number;
  onChangePage: (page: number) => void;
  defaultCount?: number;
}
function PaginationCustom({ page, defaultCount = 1, onChangePage }: IPaginationCustomProps) {
  const pathName = usePathname();
  const searchParams = useSearchParams();

  const generatePageHref = useCallback(
    (page: Nullable<number>) => {
      const currentParams = new URLSearchParams(searchParams.toString());
      if (page !== 1) {
        currentParams.set("page", `${page}`);
      } else {
        currentParams.delete("page");
      }
      return `${pathName}?${currentParams.toString()}`;
    },
    [pathName, searchParams],
  );

  return (
    <Pagination
      count={defaultCount}
      color="primary"
      onChange={(_, value) => onChangePage(value)}
      page={page}
      shape="rounded"
      disabled={defaultCount === 0}
      showFirstButton
      showLastButton
      siblingCount={0}
      renderItem={(item) => {
        const pageHref = generatePageHref(item.page);
        return (
          <PaginationItem
            component={Link}
            href={pageHref}
            slots={{
              previous: KeyboardArrowLeftRoundedIcon,
              next: KeyboardArrowRightRoundedIcon,
              first: KeyboardDoubleArrowLeftRoundedIcon,
              last: KeyboardDoubleArrowRightRoundedIcon,
            }}
            {...item}
          />
        );
      }}
      sx={{
        "& .MuiPaginationItem-root": {
          fontWeight: 400,
          fontSize: { xs: "12px", sm: "14px" },
          lineHeight: { xs: "20px", sm: "28px" },
          margin: { xs: "0px 2px", sm: "0px 4px" },
          border: "unset",
          borderRadius: "8px",
          height: {
            xs: "24px !important",
            sm: "32px !important",
            md: "32px !important",
            lg: "40px !important",
            xl: "40px !important",
          },
          width: {
            xs: "24px !important",
            sm: "32px !important",
            md: "32px !important",
            lg: "40px !important",
            xl: "40px !important",
          },
          padding: { xs: "8px", sm: "8px" },
          background: color.gray110,
          "&.Mui-selected": {
            background: color.redGradient,
            color: color.white,
          },
          "&.MuiPaginationItem-ellipsis": {
            borderRadius: "8px",
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "end",
            justifyContent: "center",
            padding: "4px 4px",
          },
        },
        // "& .MuiPaginationItem-previousNext": {
        //   height: "32px !important",
        //   width: "32px",
        //   borderRadius: "1px",
        // },
        "& .MuiPagination-ul": {},
      }}
    />
  );
}

export default PaginationCustom;
